import type { v5 } from '@contracts'
import AiOutputContainer from './AiOutputContainer/AiOutputContainer'
import clsx from 'clsx'
import { getFormattedDate } from 'utils'
import { tabsData } from 'pages/ViewTask/utils'
import { sanitiseProductData, sanitiseBrandData, sanitiseRetailerData, sanitiseCategoryData } from './utils'
import type { AiOutputVersionDetails } from './AiOutput.d'
import type { pdpTabIds } from 'components/Tabs/tabs-type.d'
import styles from './AiOutput.module.scss'
import Banner from 'components/Banner'
import MissingJobDataMessage from './MissingJobDataMessage'
interface RetailersDetailDataWithVersion extends v5.VersionDetailOutput {
  latest_version: number | string
  version_used: number | string
}

interface AiOutputProps {
  activeTab: pdpTabIds
  jobData?: v5.JobsDetailData | null
  retailerData?: RetailersDetailDataWithVersion | null
  categoryData?: any
  latestRetailerData?: v5.RetailersDetailData | null
  latestCategoryData?: v5.CategoriesDetailData | null
}

const AiOutput = ({
  activeTab,
  jobData,
  retailerData,
  categoryData,
  latestCategoryData,
  latestRetailerData,
}: AiOutputProps) => {
  const tabDataName = tabsData.find(tabData => tabData.view === activeTab)?.displayText

  if (!jobData?.config) return <MissingJobDataMessage />

  const { retailer, brand, product, category } = jobData.config!
  switch (activeTab) {
    case 'brand_rules':
      if (!brand) return <MissingJobDataMessage />
      break
    case 'product_info':
      if (!product) return <MissingJobDataMessage />
      break
    case 'retailer_rules':
      if (!retailer) return <MissingJobDataMessage />
      break
    case 'category_rules':
      if (!category) return <MissingJobDataMessage />
      break
  }

  const sanitisedProductData = sanitiseProductData(product)
  const sanitisedBrandData = sanitiseBrandData(brand)
  const sanitisedRetailerData = sanitiseRetailerData(retailerData!)
  const sanitisedCategoryData = sanitiseCategoryData(categoryData)

  // For brand rules, we always show the banner
  const shouldShowBrandVersionBanner = activeTab === 'brand_rules'

  const dataToUseInRender = () => {
    switch (activeTab) {
      case 'brand_rules':
        return {
          data: sanitisedBrandData,
          versionDetails: {
            version: brand?.version,
            created: brand?.created_at,
          } as AiOutputVersionDetails,
        }
      case 'product_info':
        return { data: sanitisedProductData, versionDetails: undefined }

      case 'retailer_rules':
        return {
          data: sanitisedRetailerData,
          versionDetails: {
            version: retailerData?.version_used,
            created: retailerData?.created_at,
          } as AiOutputVersionDetails,
        }
      case 'category_rules':
        return {
          data: sanitisedCategoryData,
          versionDetails: {
            version: categoryData?.version_used,
            created: categoryData?.created_at,
          } as AiOutputVersionDetails,
        }
    }
  }

  const renderEntity = dataToUseInRender()

  const safeGetFormattedDate = (dateString?: string) => {
    if (!dateString) return ''
    try {
      return getFormattedDate(dateString)
    } catch (e) {
      return ''
    }
  }

  // Get the appropriate version info based on active tab
  const getVersionInfo = () => {
    if (activeTab === 'retailer_rules') {
      return {
        latestVersion: retailerData?.latest_version,
        versionUsed: retailerData?.version_used,
        // Use data from the latest version
        updatedAt: latestRetailerData?.updated_at || '',
        userName: latestRetailerData?.update_info?.user_name || '',
      }
    } else if (activeTab === 'category_rules') {
      return {
        latestVersion: categoryData?.latest_version,
        versionUsed: categoryData?.version_used,
        // Use data from the latest version
        updatedAt: latestCategoryData?.updated_at || '',
        userName: latestCategoryData?.update_info?.user_name || '',
      }
    }
    return { latestVersion: '', versionUsed: '', updatedAt: '', userName: '' }
  }

  const versionInfo = getVersionInfo()
  const shouldShowVersionBanner =
    (activeTab === 'retailer_rules' && retailerData?.latest_version !== retailerData?.version_used) ||
    (activeTab === 'category_rules' && categoryData?.latest_version !== categoryData?.version_used)

  // Only show created date and user if they exist
  const createdInfo =
    versionInfo.updatedAt || versionInfo.userName
      ? `(created: ${safeGetFormattedDate(versionInfo.updatedAt)}${versionInfo.userName ? ` by ${versionInfo.userName}` : ''})`
      : ''

  return (
    <section className={styles.aiOutputContainer} data-testid="ai-page">
      <section className={clsx(styles.aiOutputContainer)} data-testid="ai-content">
        {/* retailer/category rules banner */}
        <Banner
          text={
            <>
              This content was generated using Version {renderEntity?.versionDetails?.version} of the {tabDataName}.
              {shouldShowVersionBanner && (
                <>
                  {' '}
                  Version {versionInfo.latestVersion} {createdInfo} is the current version.
                </>
              )}
            </>
          }
          type="info"
          className={styles.banner}
          shouldBannerShow={shouldShowVersionBanner}
          isBannerFixedPosition={false}
        />

        {/* brand rules banner */}
        <Banner
          text={
            <>
              This data is based in Version {renderEntity?.versionDetails?.version} of the Brand rules, created:{' '}
              {safeGetFormattedDate(renderEntity?.versionDetails?.created)}.
            </>
          }
          type="info"
          className={styles.banner}
          shouldBannerShow={shouldShowBrandVersionBanner}
          isBannerFixedPosition={false}
        />

        <AiOutputContainer outputData={renderEntity?.data} />
      </section>
    </section>
  )
}

export default AiOutput
